/**
 * EPCIS overage on the Stock screen. The vendor's EPCIS listed more units of a product than the PO
 * ordered; the extra units are not physically here. Stocking decides which serials arrived — the
 * server refuses a scan that would stock more than the PO ordered — and whatever is not stocked is
 * marked not received when the PO closes. Nothing here blocks: it tells the stocker what to expect.
 */

export type StockingLine = {
  productId: string;
  orderedQuantity: number;
  stockedQuantity: number;
  epcisQuantity?: number | null;
  overageQuantity: number;
  product?: { id?: string | null; title?: string | null } | null;
};

/** Present = the PO-level banner shows (some product on the PO has an overage). */
export type StockOverageNotice = {
  /** One per overage product being stocked by this scan. */
  lines: {
    productId: string;
    title: string | null;
    message: string;
    stockedQuantity: number;
    orderedQuantity: number;
  }[];
};

export const overageLineMessage = (line: StockingLine) =>
  `EPCIS lists ${line.epcisQuantity ?? line.orderedQuantity + line.overageQuantity}, PO ordered ${
    line.orderedQuantity
  }. Stock only what's physically here; unscanned serials will be marked not received.`;

/**
 * What to show for a scan of `scannedProductIds` (the unit's product, or every product inside a
 * scanned case) on a PO with `progress`. Null when the PO has no overage — no notice at all.
 */
export function stockOverageNotice(
  progress: StockingLine[] | null | undefined,
  scannedProductIds: (string | null | undefined)[]
): StockOverageNotice | null {
  const overageLines = (progress || []).filter((line) => line.overageQuantity > 0);
  if (overageLines.length === 0) return null;
  const scanned = new Set(scannedProductIds.filter(Boolean).map(String));
  return {
    lines: overageLines
      .filter((line) => scanned.has(String(line.productId)))
      .map((line) => ({
        productId: String(line.productId),
        title: line.product?.title ?? null,
        message: overageLineMessage(line),
        stockedQuantity: line.stockedQuantity,
        orderedQuantity: line.orderedQuantity
      }))
  };
}
